import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Restore persisted image filter selection
    this.#restoreImageFilterSelection();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Updates the image filter mode across all product cards.
   * value "1" forces first image; value "2" forces secondary image.
   * @param {Event} event
   */
  updateImageFilter = ({ target }) => {
    if (!(target instanceof HTMLInputElement)) return;
    const value = target.value;
    this.#setImageFilter(value);
  };

  /**
   * Apply image filter mode and persist it.
   * @param {"1"|"2"} value
   */
  #setImageFilter(value) {
    const { grid } = this.refs;
    if (!grid) return;

    // Toggle an attribute on the grid for CSS/logic hooks
    grid.setAttribute('data-image-filter', value);

    // Iterate product cards and adjust their slideshows
    const productCards = grid.querySelectorAll('product-card');
    productCards.forEach((card) => {
      const slideshow = card?.querySelector('slideshow-component');
      if (!(slideshow instanceof HTMLElement)) return;

      /** @type {any} */
      const component = /** @type {any} */ (slideshow);
      // Ensure the custom element API is ready
      if (typeof component.select !== 'function') return;

      // For value "1" select the default slide; for "2" select the second slide
      try {
        if (value === '2') {
          component.select(1, undefined, { animate: false });
        } else {
          // Go back to the default slide id when possible
          const defaultSlide = component.defaultSlide;
          const slideId = defaultSlide?.getAttribute?.('slide-id');
          if (slideId) {
            component.select({ id: slideId }, undefined, { animate: false });
          } else {
            component.select(0, undefined, { animate: false });
          }
        }
      } catch (e) {
        // no-op
      }

      // Mark card so hover handlers can be guarded
      if (value === '2') {
        card.setAttribute('data-force-secondary', 'true');
      } else {
        card.removeAttribute('data-force-secondary');
      }
    });

    try {
      sessionStorage.setItem('product-grid-image-filter', value);
    } catch {}
  }

  /** Restore the persisted image filter selection and update checked state */
  #restoreImageFilterSelection() {
    try {
      const value = sessionStorage.getItem('product-grid-image-filter');
      if (!value) return;

      // Update radios
      const radios = this.querySelectorAll('input[type="radio"][name="image-filter"]');
      radios.forEach((radio) => {
        if (radio instanceof HTMLInputElement) radio.checked = radio.value === value;
      });

      this.#setImageFilter(value);
    } catch {}
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
